import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  Search,
  Hash
} from "lucide-react";
import {
  getAllFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  FAQQuestion
} from "@/lib/faq-service";

const FAQManagement = () => {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState<FAQQuestion[]>([]);
  const [filteredFAQs, setFilteredFAQs] = useState<FAQQuestion[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingFAQ, setEditingFAQ] = useState<FAQQuestion | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    keywords: "",
    category: "",
    priority: 0,
    is_active: true
  });

  useEffect(() => {
    loadFAQs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [faqs, searchQuery]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const data = await getAllFAQs();
      setFaqs(data);
    } catch (error: any) {
      toast({
        title: "Error Loading FAQs",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchQuery.trim()) {
      setFilteredFAQs(faqs);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = faqs.filter(faq =>
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.keywords.some(k => k.toLowerCase().includes(query)) ||
      faq.category?.toLowerCase().includes(query)
    );

    setFilteredFAQs(filtered);
  };

  const handleOpenDialog = (faq?: FAQQuestion) => {
    if (faq) {
      setEditingFAQ(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        keywords: faq.keywords.join(", "),
        category: faq.category || "",
        priority: faq.priority,
        is_active: faq.is_active
      });
    } else {
      setEditingFAQ(null);
      setFormData({
        question: "",
        answer: "",
        keywords: "",
        category: "",
        priority: 0,
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingFAQ(null);
  };

  const handleSubmit = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast({
        title: "Validation Error",
        description: "Question and answer are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const keywordsArray = formData.keywords
        .split(",")
        .map(k => k.trim())
        .filter(k => k.length > 0);

      if (editingFAQ) {
        // Update existing FAQ
        const success = await updateFAQ(editingFAQ.id, {
          question: formData.question,
          answer: formData.answer,
          keywords: keywordsArray,
          category: formData.category || null,
          priority: formData.priority,
          is_active: formData.is_active
        });

        if (success) {
          toast({
            title: "FAQ Updated",
            description: "FAQ has been updated successfully"
          });
          handleCloseDialog();
          loadFAQs();
        } else {
          throw new Error("Failed to update FAQ");
        }
      } else {
        // Create new FAQ
        const newFAQ = await createFAQ(
          formData.question,
          formData.answer,
          keywordsArray,
          formData.category || null,
          formData.priority
        );

        if (newFAQ) {
          toast({
            title: "FAQ Created",
            description: "New FAQ has been created successfully"
          });
          handleCloseDialog();
          loadFAQs();
        } else {
          throw new Error("Failed to create FAQ");
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      const success = await deleteFAQ(id);
      if (success) {
        toast({
          title: "FAQ Deleted",
          description: "FAQ has been deleted successfully"
        });
        loadFAQs();
      } else {
        throw new Error("Failed to delete FAQ");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const activeFAQs = faqs.filter(f => f.is_active).length;
  const totalUsage = faqs.reduce((sum, f) => sum + f.usage_count, 0);

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total FAQs</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{faqs.length}</div>
            <p className="text-xs text-muted-foreground">
              {activeFAQs} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsage.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              FAQ matches served
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Saved</CardTitle>
            <Hash className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{(totalUsage * 5).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Via FAQ system
            </p>
          </CardContent>
        </Card>
      </div>

      {/* FAQ List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                FAQ Management
              </CardTitle>
              <CardDescription>
                Manage frequently asked questions to save user credits
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add FAQ
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredFAQs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No FAQs found
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {filteredFAQs.map((faq) => (
                <Card key={faq.id} className={!faq.is_active ? 'opacity-50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h4 className="font-semibold text-sm">{faq.question}</h4>
                          {faq.category && (
                            <Badge variant="outline">{faq.category}</Badge>
                          )}
                          <Badge variant="secondary">
                            Priority: {faq.priority}
                          </Badge>
                          <Badge variant="outline" className="bg-green-500/10 text-green-600">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {faq.usage_count} uses
                          </Badge>
                          {!faq.is_active && (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </div>

                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {faq.answer}
                        </p>

                        {faq.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {faq.keywords.map((keyword, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {keyword}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(faq)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(faq.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFAQ ? "Edit FAQ" : "Create New FAQ"}
            </DialogTitle>
            <DialogDescription>
              {editingFAQ
                ? "Update the FAQ details below"
                : "Add a new frequently asked question"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="What is..."
              />
            </div>

            <div>
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="The answer is..."
                rows={5}
              />
            </div>

            <div>
              <Label htmlFor="keywords">Keywords (comma-separated)</Label>
              <Input
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="General, Pricing, etc."
                />
              </div>

              <div>
                <Label htmlFor="priority">Priority (0-10)</Label>
                <Input
                  id="priority"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="is_active">Status</Label>
              <Select
                value={formData.is_active ? "active" : "inactive"}
                onValueChange={(v) => setFormData({ ...formData, is_active: v === "active" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingFAQ ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQManagement;
